<template>
  <!-- 无数据：只展示空状态，隐藏三栏布局 -->
  <div
    v-if="!caseListData || caseListData.length === 0"
    class="empty-full-container"
  >
    <EmptyData />
  </div>
  <div v-else class="search-workbench-wrapper">
    <!-- 顶部搜索栏 -->
    <WorkbenchHeader
      :keyword="keyword"
      :download-disabled="!reportFinished"
      :rawMdText="rawMdText"
      @back="emit('back')"
      @download="handleDownload"
    />

    <!-- 三栏布局 -->
    <div class="search-workbench">
      <!-- 左侧目录 -->
      <WorkbenchLeft
        :menu-list="menuList"
        :active-key="activeKey"
        @change="handleMenu"
      />

      <!-- 中间 -->
      <WorkbenchCenter
        ref="centerRef"
        :analysis-data="analysisData"
        :caseListData="caseListData"
        :keyword="keyword"
        :thinkHtml="thinkHtml"
        :thinkStreaming="thinkStreaming"
        :rawThinkText="rawThinkText"
        :year="year"
        :courtName="courtName"
        :analyzeApiUrl="analyzeApiUrl"
        :groupId="groupId"
        :analyzeReqData="analyzeReqData"
        @detail="handleDetail"
        @goView="handlegoView"
        @reportFinish="onReportFinish"
        @favoriteChange="(e) => emit('favoriteChange', e)"
      />

      <!-- 右侧 -->
      <WorkbenchRight
        ref="workbenchRightRef"
        :keyword="keyword"
        @send="handleAskAI"
        @viewDetail="viewDetail"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, nextTick } from "vue";

import WorkbenchHeader from "./WorkbenchHeader.vue";

import WorkbenchLeft from "./WorkbenchLeft.vue";
import WorkbenchCenter from "./WorkbenchCenter.vue";
import WorkbenchRight from "./WorkbenchRight.vue";
import EmptyData from "./EmptyData.vue";
import { getApiBaseUrl } from "@/utils/env";

import {
  analysisData,
  // caseListData
} from "./mock";

// import { Export } from '../../api/case/case.js'
import { message } from "ant-design-vue";
import FileHandler from "../../utils/fileHandler";
import dayjs from "dayjs";

defineProps({
  keyword: {
    type: String,
    default: "",
  },
  year: {
    type: Number,
    default: 0,
  },
  courtName: {
    type: String,
    default: "",
  },
  caseListData: {
    type: Array,
    default: () => [], // 工厂函数返回空数组
  },
  thinkHtml: {
    type: String,
    default: "",
  },
  thinkStreaming: {
    type: Boolean,
    default: false,
  },
  rawThinkText: {
    type: String,
    default: "",
  },
  analyzeApiUrl: {
    type: String,
    default: "",
  },
  groupId: {
    type: String,
    default: "",
  },
  analyzeReqData: {
    type: Object,
    default: null,
  },
});
// console.log(caseListData,'caseListData')
const emit = defineEmits(["detail", "back", "goView", "search", "viewDetail", "favoriteChange"]);

const handlegoView = (item) => {
  console.log(item, "item1");
  emit("goView", item);
};
const viewDetail = (item) => {
  console.log(item, "item1");
  emit("viewDetail", item);
};
const centerRef = ref();
const workbenchRightRef = ref();

// 从子组件 MdFileRead 获取纯文本 md 内容（用于复制）
const rawMdText = computed(
  () => centerRef.value?.mdFileReadRef?.rawMdText || "",
);

// 报告是否已完成（流式数据接收完毕）
const reportFinished = ref(false);
const extensionMap = { pdf: "pdf", word: "docx" };

// 监听 MdFileRead 的 isFinish 状态
watch(
  () => centerRef.value?.mdFileReadRef,
  (mdFileRead) => {
    if (mdFileRead) {
      // MdFileRead 实例就绪后，立即检查当前 isFinish 值
      if (mdFileRead.isFinish) {
        reportFinished.value = true;
      }
      // 如果还没完成，watch 它的 isFinish ref
      watch(
        () => mdFileRead.isFinish,
        (val) => {
          if (val) {
            reportFinished.value = true;
          }
        },
        { immediate: true },
      );
    }
  },
  { immediate: true },
);

// MdFileRead 的 streamFinish 事件也会向上传递，再补一个兜底
const onReportFinish = () => {
  reportFinished.value = true;
};

/**
 * 左侧目录
 */
const menuList = [
  {
    key: "answer",
    title: "报告",
  },
  // {
  //   key: "focus",
  //   title: "争议焦点分析"
  // },
  // {
  //   key: "core",
  //   title: "核心案例剖析"
  // },
  {
    key: "case",
    title: "精选案例",
  },
];

/**
 * 当前目录
 */
const activeKey = ref("answer");

/**
 * 左侧菜单点击
 */
let scrollLockTimer = null;
const handleMenu = (key) => {
  activeKey.value = key;

  // 滚动期间临时禁用 scroll 监听，避免平滑动画中竞争
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  if (scrollLockTimer) {
    clearTimeout(scrollLockTimer);
  }

  // 报告相关项：滚动到 MdFileRead 区域
  if (key === "answer" || key === "core") {
    centerRef.value?.scrollToReport?.();
    // 等滚动动画结束后重新启用 observer
    scrollLockTimer = setTimeout(() => {
      setupScrollObserver();
      scrollLockTimer = null;
    }, 500);
    return;
  }

  const element = document.getElementById(key);

  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  // 等滚动动画结束后重新启用 observer
  scrollLockTimer = setTimeout(() => {
    setupScrollObserver();
    scrollLockTimer = null;
  }, 500);
};

/**
 * IntersectionObserver：监听中间内容区域的滚动，自动更新左侧目录高亮
 */
let observer = null;

const setupScrollObserver = () => {
  // 断开旧 observer
  if (observer) {
    observer.disconnect();
    observer = null;
  }

  const centerEl = centerRef.value?.$el;
  if (!centerEl) return;

  // 直接监听 scroll 事件，通过滚动位置判断当前可见区域
  const onScroll = () => {
    if (scrollLockTimer) return; // 点击菜单触发的平滑滚动期间不处理

    const caseEl = centerEl.querySelector("#case");
    const reportEl = centerEl.querySelector("#report");
    if (!caseEl || !reportEl) return;

    const scrollTop = centerEl.scrollTop;
    const caseOffsetTop = caseEl.offsetTop;
    // const reportOffsetTop = reportEl.offsetTop

    // 如果 #case 的顶部距离滚动容器顶部 <= 5px，说明已滚动到精选案例区域
    if (scrollTop >= caseOffsetTop - 5) {
      if (activeKey.value !== "case") {
        activeKey.value = "case";
      }
    } else if (scrollTop < caseOffsetTop - 5) {
      // 否则还在报告区域
      if (activeKey.value !== "answer") {
        activeKey.value = "answer";
      }
    }
  };

  centerEl.addEventListener("scroll", onScroll, { passive: true });
  // 把 cleanup 存到 observer 变量里复用
  observer = {
    disconnect: () => centerEl.removeEventListener("scroll", onScroll),
  };

  // 初始化时立即执行一次
  onScroll();
};

// 等待 centerRef 就绪后再设置 observer
watch(
  centerRef,
  (val) => {
    if (val) {
      nextTick(() => {
        setupScrollObserver();
      });
    }
  },
  { immediate: true },
);

// caseListData 到达后（#case 元素渲染完成）重新建立 observer
watch(
  () => centerRef.value?.$el?.querySelector("#case"),
  (caseEl) => {
    if (caseEl) {
      nextTick(() => {
        setupScrollObserver();
      });
    }
  },
);

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
});

/**
 * 下载报告
 */
const handleDownload = async (fileType) => {
  const mdFileRead = centerRef.value?.mdFileReadRef;
  if (!mdFileRead || !mdFileRead.rawMdText) {
    message.warning("暂无报告内容可下载");
    return;
  }

  try {
    const apiBaseUrl = getApiBaseUrl() || "";
    const token = localStorage.getItem("token");
    const response = await fetch(`${apiBaseUrl}api/ReportExport/Export`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({
        markdownContent: mdFileRead.rawMdText,
        fileType,
      }),
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      message.error(errData.errMsg || "下载失败");
      return;
    }

    const blob = await response.blob();
    const ext = extensionMap[fileType] || "pdf";
    const filename = `分析报告_${dayjs().format("YYYYMMDD_HHmmss")}.${ext}`;
    FileHandler.saveFile(blob, filename);
    message.success("下载成功");
  } catch (err) {
    console.error("下载失败：", err);
    message.error("下载失败");
  }
};

/**
 * 查看案例详情
 */
const handleDetail = (item) => {
  emit("detail", item);
};

/**
 * AI追问
 */
const handleAskAI = (question) => {
  console.log("AI追问：", question);
};

// 案号点击：定位到案例列表
// const handleCaseClick = (ah) => {
//   centerRef.value?.caseListPanelRef?.value?.scrollToCase?.(ah)
// }

const startAnalyze = () => {
  // 新搜索：重置右侧追问数据
  workbenchRightRef.value?.reset?.();
  centerRef.value?.startAnalyze?.();
};

defineExpose({
  startAnalyze,
  cancelAnalyze() {
    centerRef.value?.cancelAnalyze?.();
  },
  rawMdText,
  workbenchRightRef,
});
</script>

<style scoped lang="less">
.search-workbench-wrapper {
  width: 100%;
  margin-top: 32px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 124px);
}

.workbench-header {
  height: 64px; /* 按你实际高度 */
  flex-shrink: 0;
}
/* 三栏 */

.search-workbench {
  display: flex;
  flex: 1;
  gap: 20px;
  min-height: 0;
}

/* 左 */

.search-workbench > :nth-child(1) {
  width: 188px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
  z-index: 10;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 中 */

.search-workbench > :nth-child(2) {
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
}

/* 右 */

.search-workbench > :nth-child(3) {
  width: 580px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
  z-index: 10;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.search-workbench > :nth-child(1),
.search-workbench > :nth-child(3) {
  flex-shrink: 0;
  height: 100%;
}
/* 1400 */

@media (max-width: 1400px) {
  .search-workbench > :nth-child(3) {
    width: 320px;
  }
}

/* 1200 */

@media (max-width: 1200px) {
  .search-workbench {
    flex-direction: column;
  }

  .search-workbench > :nth-child(1),
  .search-workbench > :nth-child(2),
  .search-workbench > :nth-child(3) {
    width: 100%;
  }
}
</style>
